import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Two-month range calendar.
 *
 * A pair of `<input type="date">` is the wrong control for a stay: it shows the browser's locale
 * rather than the hotel's, hides the weekend the guest is actually looking for, and cannot say
 * "three nights". This shows both months at once, marks the range, and states the length.
 *
 * Fully keyboard operable — arrows move by day, PageUp/PageDown by month, Enter picks. The
 * roving-tabindex grid is what a screen reader expects from a calendar, and a booking funnel is
 * a bad place to be the exception.
 */

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export type DateRange = { checkin: string; checkout: string };

function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function fromIso(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth() + months, 1);
  return next;
}

/** Days of `month`, padded so the grid starts on a Monday. */
function monthGrid(month: Date): (Date | null)[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = Array.from({ length: lead }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), day));
  }
  return cells;
}

function nightsBetween(from: string, to: string): number {
  return Math.round((fromIso(to).getTime() - fromIso(from).getTime()) / 86_400_000);
}

type Props = {
  value: Partial<DateRange>;
  maxNights: number;
  onChange: (range: DateRange) => void;
};

export function DateRangePicker({ value, maxNights, onChange }: Props) {
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const [firstMonth, setFirstMonth] = useState(() =>
    value.checkin ? addMonths(fromIso(value.checkin), 0) : addMonths(today, 0),
  );
  // While picking, the guest has a check-in but no check-out yet.
  const [pendingCheckin, setPendingCheckin] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [focused, setFocused] = useState<string>(value.checkin ?? iso(today));
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value.checkin) setPendingCheckin(null);
  }, [value.checkin]);

  const rangeStart = pendingCheckin ?? value.checkin ?? null;
  const rangeEnd = pendingCheckin ? (hovered ?? null) : (value.checkout ?? null);

  const maxCheckout = rangeStart ? iso(addDays(fromIso(rangeStart), maxNights)) : null;

  function isDisabled(date: Date): boolean {
    if (iso(date) < iso(today)) return true;
    // Second click: only dates that make a legal stay stay clickable.
    if (pendingCheckin && (iso(date) <= pendingCheckin || (maxCheckout && iso(date) > maxCheckout))) return true;
    return false;
  }

  function select(date: Date) {
    const picked = iso(date);
    if (!pendingCheckin) {
      setPendingCheckin(picked);
      setHovered(null);
      // Picking the arrival disables that very cell — it is no longer a legal departure — and a
      // disabled button drops focus to the body. Someone booking by keyboard would be thrown out
      // of the calendar after their first date, so focus moves on to the first possible
      // departure instead.
      setFocused(iso(addDays(date, 1)));
      return;
    }
    onChange({ checkin: pendingCheckin, checkout: picked });
    setPendingCheckin(null);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    const current = fromIso(focused);
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (event.key in moves) {
      event.preventDefault();
      const next = addDays(current, moves[event.key]);
      setFocused(iso(next));
      if (next < firstMonth || next >= addMonths(firstMonth, 2)) setFirstMonth(addMonths(next, 0));
      return;
    }
    if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      const delta = event.key === "PageUp" ? -1 : 1;
      setFirstMonth(addMonths(firstMonth, delta));
      setFocused(iso(addMonths(current, delta)));
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!isDisabled(current)) select(current);
    }
  }

  // Tracks whether the guest is navigating by keyboard, so the calendar never steals focus from
  // elsewhere on the page — only ever hands it back to itself.
  const hadFocus = useRef(false);

  useEffect(() => {
    const grid = gridRef.current;
    const node = grid?.querySelector<HTMLElement>(`[data-date="${focused}"]`);
    if (!grid || !node || document.activeElement === node) return;
    const active = document.activeElement;
    const focusWasOurs = grid.contains(active);
    // `active === body` means a cell we just disabled dropped it — reclaiming that is the whole
    // point, but only if the calendar had focus to begin with.
    const focusWasLost = hadFocus.current && (active === null || active === document.body);
    if (focusWasOurs || focusWasLost) node.focus();
  }, [focused]);

  const months = [firstMonth, addMonths(firstMonth, 1)];
  const nights = value.checkin && value.checkout ? nightsBetween(value.checkin, value.checkout) : null;

  return (
    <div className="calendar">
      <div className="calendar-head">
        <button
          type="button"
          className="calendar-nav"
          aria-label="Vorheriger Monat"
          disabled={addMonths(firstMonth, -1) < new Date(today.getFullYear(), today.getMonth(), 1)}
          onClick={() => setFirstMonth(addMonths(firstMonth, -1))}
        >
          ‹
        </button>
        <p className="calendar-title" aria-live="polite">
          {pendingCheckin
            ? "Wann reisen Sie ab?"
            : nights
              ? `${nights} ${nights === 1 ? "Nacht" : "Nächte"}`
              : "Wann reisen Sie an?"}
        </p>
        <button
          type="button"
          className="calendar-nav"
          aria-label="Nächster Monat"
          onClick={() => setFirstMonth(addMonths(firstMonth, 1))}
        >
          ›
        </button>
      </div>

      <div className="calendar-months" ref={gridRef} onKeyDown={onKeyDown} onMouseLeave={() => setHovered(null)}>
        {months.map((month) => (
          <div className="calendar-month" key={iso(month)}>
            <h3>
              {MONTHS[month.getMonth()]} {month.getFullYear()}
            </h3>
            <div className="calendar-weekdays" aria-hidden="true">
              {WEEKDAYS.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>
            <div className="calendar-grid" role="grid" aria-label={`${MONTHS[month.getMonth()]} ${month.getFullYear()}`}>
              {monthGrid(month).map((date, index) => {
                if (!date) return <span key={`pad-${index}`} className="calendar-pad" />;
                const key = iso(date);
                const disabled = isDisabled(date);
                const isStart = key === rangeStart;
                const isEnd = key === rangeEnd;
                const inRange = Boolean(
                  rangeStart && rangeEnd && key > rangeStart && key < rangeEnd,
                );
                const classes = [
                  "calendar-day",
                  isStart ? "is-start" : "",
                  isEnd ? "is-end" : "",
                  inRange ? "is-between" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <button
                    key={key}
                    type="button"
                    role="gridcell"
                    data-date={key}
                    className={classes}
                    disabled={disabled}
                    tabIndex={key === focused ? 0 : -1}
                    aria-selected={isStart || isEnd}
                    aria-label={`${date.getDate()}. ${MONTHS[date.getMonth()]} ${date.getFullYear()}`}
                    onFocus={() => {
                      hadFocus.current = true;
                      setFocused(key);
                    }}
                    onBlur={() => {
                      // Leaving the calendar for good — a later re-render must not pull focus back.
                      if (!gridRef.current?.contains(document.activeElement)) hadFocus.current = false;
                    }}
                    onMouseEnter={() => pendingCheckin && setHovered(key)}
                    onClick={() => select(date)}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="calendar-summary">
        {value.checkin && value.checkout && !pendingCheckin ? (
          <>
            <strong>{fromIso(value.checkin).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "long" })}</strong>
            {" – "}
            <strong>{fromIso(value.checkout).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "long" })}</strong>
          </>
        ) : (
          <span className="muted">Höchstens {maxNights} Nächte online buchbar.</span>
        )}
      </p>
    </div>
  );
}
